import { ArrowRight } from "lucide-react";

import { Badge } from "@/shared/ui/badge";
import { Button } from "@/shared/ui/button";
import { CARD_WALL_RADIUS } from "@/shared/ui/cardWall";
import { QueryErrorBanner } from "@/shared/ui/QueryErrorBanner";
import { Skeleton } from "@/shared/ui/skeleton";
import { cn } from "@/shared/lib/utils";

import type { Template } from "../../api/types";
import { fieldSpecText, schemaFieldCount } from "../../model/schemaLabels";

/**
 * 屏 2 右栏「{模板名} · 需要准备的资料」:按 schema 分组列字段,每行「label · type 人话」。
 *
 * ★ 项数 = spec 级字段总数(repeat 组按 spec 计一次,附录 A.11),从 `schema` 现算,
 *   不信 summary 的 `fieldCount`(两处漂了这里先看得见)。
 * ★ 设计稿底部「预计知识库可预填 21 / 27 项」没有数据口 —— 不画,不编。
 */
export function SchemaChecklist({
  template,
  error,
  onRetry,
  onStart,
}: {
  /** null = 还在拉。 */
  template: Template | null;
  error: string | null;
  onRetry: () => void;
  onStart: () => void;
}) {
  const title = template ? `${template.name} · 需要准备的资料` : "需要准备的资料";
  return (
    <aside
      aria-labelledby="website-schema-title"
      className={cn("flex flex-col gap-4 border border-border bg-card p-4", CARD_WALL_RADIUS)}
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 id="website-schema-title" className="text-sm font-semibold text-foreground">
          {title}
        </h2>
        {template && (
          <Badge variant="secondary" className="normal-case tracking-normal">
            {schemaFieldCount(template.schema)} 项
          </Badge>
        )}
      </div>
      <p className="text-xs leading-relaxed text-muted-foreground">
        这份清单就是下一步的采集表单,也是 AI 追问的提纲。知识库里有的会自动预填并标出处。
      </p>

      {error ? (
        <QueryErrorBanner isError message={error} onRetry={onRetry} />
      ) : !template ? (
        <div className="space-y-2">
          <Skeleton className="h-5 w-1/3" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-5/6" />
        </div>
      ) : (
        <div className="space-y-4">
          {template.schema.map((g) => (
            <section key={g.group} aria-label={g.group} className="space-y-1.5">
              <h3 className="text-xs font-semibold text-foreground">{g.group}</h3>
              <ul className="space-y-1">
                {g.fields.map((f) => (
                  <li
                    key={f.path}
                    className="border-b border-border/50 pb-1 text-xs last:border-b-0"
                  >
                    <div className="flex items-baseline justify-between gap-3">
                      <span className="text-foreground">{f.label}</span>
                      <span className="text-right text-2xs text-muted-foreground">
                        {fieldSpecText(f)}
                      </span>
                    </div>
                    {/* 模板作者的提示(如「证书类内容只从知识库已审核资料取」)单独一行,不挤进右侧那句。 */}
                    {f.hint && (
                      <p className="mt-0.5 text-2xs leading-snug text-muted-foreground/80">
                        {f.hint}
                      </p>
                    )}
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>
      )}

      <Button className="w-full" disabled={!template} onClick={onStart}>
        用这套建站 · 一键生成
        <ArrowRight />
      </Button>
    </aside>
  );
}
